import type {
  AdminAppSettings,
  AdminCostEstimate,
  AdminDashboardSummary,
  AdminDeploymentInfo,
  AdminFeatureFlags,
} from '@ridemaadi/types';
import { apiFetch } from '../lib/api-client';

const PREFIX = '/admin';

export function checkAdminAccess() {
  return apiFetch<{ access: true }>(`${PREFIX}/access`);
}

export function fetchDashboard() {
  return apiFetch<AdminDashboardSummary>(`${PREFIX}/dashboard`);
}

export function fetchRideOperations() {
  return apiFetch<{
    liveRides: Array<{
      id: string;
      title: string;
      status: string;
      started_at: string | null;
      captain_name: string | null;
      riders: number;
    }>;
    activeRiders: number;
    sosAlerts: Array<{ rideId: string; message: string }>;
    offlineRiders: number;
    poorGps: number;
    lowBattery: number;
    navigationSessions: number;
    refreshedAt: string;
  }>(`${PREFIX}/rides/operations`);
}

export function fetchHealth() {
  return apiFetch<{
    infrastructure: Record<string, string>;
    google: Record<string, string>;
    performance: {
      latencyMsP50: number;
      latencyMsP95: number;
      latencyMsP99: number;
      memoryMb: number | null;
      cpuPercent: number | null;
    };
    errors: Array<{ message: string; at: string }>;
    refreshedAt: string;
  }>(`${PREFIX}/health`);
}

export function fetchUsers(q?: string) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  return apiFetch<{
    total: number;
    active: number;
    blocked: number;
    items: Array<{
      id: string;
      email: string;
      displayName: string | null;
      role: string;
      lastLoginAt: string | null;
      createdAt: string;
      suspended: boolean;
    }>;
  }>(`${PREFIX}/users${qs}`);
}

export function suspendUser(id: string) {
  return apiFetch<{ suspended: true }>(`${PREFIX}/users/${id}/suspend`, { method: 'POST' });
}

export function unsuspendUser(id: string) {
  return apiFetch<{ suspended: false }>(`${PREFIX}/users/${id}/unsuspend`, { method: 'POST' });
}

export function fetchRides(status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiFetch<{
    items: Array<{
      id: string;
      title: string;
      status: string;
      ride_date: string;
      start_time: string;
      created_at: string;
      started_at: string | null;
      ended_at: string | null;
    }>;
  }>(`${PREFIX}/rides${qs}`);
}

export function fetchMapsUsage() {
  return apiFetch<{
    todaysLoads: number;
    placesSearches: number;
    routes: number;
    reverseGeocoding: number;
    directions: number;
    quotaRemainingPct: number;
    estimatedMonthlyCostUsd: number;
    apiErrors: number;
    refreshedAt: string;
  }>(`${PREFIX}/maps`);
}

export function fetchNotifications() {
  return apiFetch<{
    pushQueue: number;
    failures: number;
    retries: number;
    delivered: number;
    broadcastsToday: number;
    refreshedAt: string;
  }>(`${PREFIX}/notifications`);
}

export function fetchFeatureFlags() {
  return apiFetch<AdminFeatureFlags>(`${PREFIX}/feature-flags`);
}

export function saveFeatureFlags(flags: AdminFeatureFlags) {
  return apiFetch<AdminFeatureFlags>(`${PREFIX}/feature-flags`, { method: 'PATCH', body: flags });
}

export function fetchLogs() {
  return apiFetch<{
    application: unknown[];
    api: unknown[];
    database: unknown[];
    gps: unknown[];
    authentication: unknown[];
    notifications: unknown[];
    errors: Array<{ message: string; at: string }>;
  }>(`${PREFIX}/logs`);
}

export function fetchDeployment() {
  return apiFetch<AdminDeploymentInfo>(`${PREFIX}/deployments`);
}

export function fetchCosts() {
  return apiFetch<AdminCostEstimate>(`${PREFIX}/costs`);
}

export function fetchAppSettings() {
  return apiFetch<AdminAppSettings>(`${PREFIX}/settings`);
}

export function saveAppSettings(settings: AdminAppSettings) {
  return apiFetch<AdminAppSettings>(`${PREFIX}/settings`, { method: 'PATCH', body: settings });
}

export function runDiagnostic(test: string) {
  return apiFetch<{ test: string; ok: boolean; at: string; message: string }>(
    `${PREFIX}/diagnostics/${test}`,
    { method: 'POST' },
  );
}
